import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Plus, Pencil, Trash2, IndianRupee } from "lucide-react";
import { Link } from "react-router-dom";
import { route } from "../../static/route";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchProcessDefinitions,
  getProcessDefinitions,
  getProcessDefinitionsLoading,
  getEditProcessDefinition,
  toggleProcessDefinitionModal,
  setEditProcessDefinition,
} from "../../features/processDefinitions/processDefinitionsSlice";
import { useCanAccess } from "../../hooks/useCanAccess";
import useConfirmPopup from "../../context/useConfirmPopup";
import { POST, PUT, DELETE } from "../../services/axios.service";
import { toastStyles } from "../../static/styles";
import { localizedProcessName } from "../../lib/utils";
import { t } from "../../locales";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../ui/table";
import ProcessDefinitionFormModal from "./ProcessDefinitionFormModal";
import { ProcessDefinitionFormInput, ProcessDefinitionProps } from "../../types/processDefinition";

const rateBasisLabel = (rateBasis: string) => {
  const key = `rateBasis${rateBasis.charAt(0).toUpperCase()}${rateBasis.slice(1)}` as keyof typeof t.processDefinitions;
  return (t.processDefinitions as any)[key] || rateBasis;
};

const ProcessDefinitionList = () => {
  const dispatch = useAppDispatch();
  const canAccess = useCanAccess();
  const confirmDelete = useConfirmPopup();

  const definitions = useAppSelector(getProcessDefinitions);
  const loading = useAppSelector(getProcessDefinitionsLoading);
  const editDefinition = useAppSelector(getEditProcessDefinition);

  const [actionLoadingId, setActionLoadingId] = useState<number>(0);

  const doFetch = () => {
    dispatch(fetchProcessDefinitions({ all: true }));
  };

  useEffect(() => {
    doFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const sortedDefinitions = [...definitions].sort((a, b) => a.sort_order - b.sort_order);

  const handleAddNew = () => {
    dispatch(setEditProcessDefinition(null));
    dispatch(toggleProcessDefinitionModal(true));
  };

  const handleEdit = (definition: ProcessDefinitionProps) => {
    dispatch(setEditProcessDefinition(definition));
    dispatch(toggleProcessDefinitionModal(true));
  };

  const handleSaveProcessDefinition = async (data: ProcessDefinitionFormInput) => {
    const payload = {
      slug: data.slug,
      name: data.name,
      name_gu: data.name_gu || null,
      sort_order: data.sort_order === "" ? null : data.sort_order,
      rate_basis: data.rate_basis,
      is_active: data.is_active,
      has_order: data.has_order,
      has_receive: data.has_receive,
      mode_policy: data.mode_policy,
      next_process_definition_ids: data.next_process_definition_ids || [],
      next_process_mandatory: data.next_process_mandatory,
    };
    try {
      const isEditing = !!editDefinition;
      const response: any = isEditing
        ? await PUT(`/v1/process-definitions/${editDefinition?.id}`, payload)
        : await POST("/v1/process-definitions", payload);
      if (response.data.status === "success") {
        toast.success(response.data.message, { ...(toastStyles as any) });
        doFetch();
      } else {
        toast.error(response.data.message, { ...(toastStyles as any) });
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t.common.somethingWentWrong, { ...(toastStyles as any) });
    }
  };

  const handleDelete = async (definition: ProcessDefinitionProps) => {
    const confirmed = await confirmDelete(
      t.processDefinitions.deleteConfirmText,
      t.processDefinitions.deleteConfirmTitle,
      t.processDefinitions.deleteConfirmButton
    );
    if (confirmed) {
      setActionLoadingId(definition.id);
      try {
        const response: any = await DELETE(`/v1/process-definitions/${definition.id}`);
        if (response.data.status === "success") {
          toast.success(response.data.message, { ...(toastStyles as any) });
          doFetch();
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
        }
      } catch (error: any) {
        toast.error(error?.response?.data?.message || t.common.somethingWentWrong, { ...(toastStyles as any) });
      }
      setActionLoadingId(0);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {canAccess("process-definitions.create") && (
          <Button
            className="h-10 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67] ml-auto"
            onClick={handleAddNew}
          >
            <Plus className="h-4 w-4 mr-2" />
            {t.processDefinitions.addNewProcess}
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">{t.processDefinitions.table.srNo}</TableHead>
                  <TableHead>{t.processDefinitions.nameLabel}</TableHead>
                  <TableHead>{t.processDefinitions.slugLabel}</TableHead>
                  <TableHead>{t.processDefinitions.rateBasisLabel}</TableHead>
                  <TableHead>{t.processDefinitions.table.flags}</TableHead>
                  <TableHead>{t.processDefinitions.table.status}</TableHead>
                  <TableHead className="text-right">{t.processDefinitions.table.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                        {t.processDefinitions.loading}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : sortedDefinitions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      {t.processDefinitions.noRecords}
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedDefinitions.map((definition, index) => (
                    <TableRow key={definition.id} className="align-top">
                      <TableCell className="font-medium text-gray-500">{index + 1}</TableCell>
                      <TableCell>
                        <span className="font-medium text-gray-900">{localizedProcessName(definition)}</span>
                      </TableCell>
                      <TableCell className="text-gray-700">{definition.slug}</TableCell>
                      <TableCell className="text-gray-700">{rateBasisLabel(definition.rate_basis)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {definition.has_order && (
                            <Badge variant="secondary">{t.processDefinitions.hasOrderBadge}</Badge>
                          )}
                          {definition.has_receive && (
                            <Badge variant="secondary">{t.processDefinitions.hasReceiveBadge}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {definition.is_active ? (
                          <Badge>{t.processDefinitions.activeBadge}</Badge>
                        ) : (
                          <Badge variant="outline">{t.processDefinitions.inactiveBadge}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canAccess("parties.rate") && (
                            <Link to={route.processDefinitionRates.replace(":id", String(definition.id))}>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                title={t.processRates.inHousePageTitle}
                              >
                                <IndianRupee className="h-4 w-4" />
                              </Button>
                            </Link>
                          )}
                          {canAccess("process-definitions.update") && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleEdit(definition)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          {canAccess("process-definitions.delete") && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-600"
                              disabled={actionLoadingId === definition.id}
                              onClick={() => handleDelete(definition)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <ProcessDefinitionFormModal handleSaveProcessDefinition={handleSaveProcessDefinition} />
    </div>
  );
};

export default ProcessDefinitionList;
